import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import { FaArrowLeft, FaEdit, FaTrash, FaImage } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const Container = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 30px;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    color: #555;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
`;

const EditButton = styled(Button)`
  background-color: #2196f3;
  color: white;
  border: none;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: #1976d2;
  }
`;

const DeleteButton = styled(Button)`
  background-color: #f44336;
  color: white;
  border: none;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: #d32f2f;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProductImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const NoImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  border-radius: 8px;
  color: #999;
  
  svg {
    font-size: 48px;
    margin-bottom: 10px;
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductName = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin-top: 0;
  margin-bottom: 10px;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  background-color: #eee;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  color: #666;
  margin-bottom: 15px;
`;

const PriceSection = styled.div`
  margin-bottom: 20px;
  
  .original-price {
    text-decoration: line-through;
    color: #999;
    margin-right: 10px;
  }
`;

const Price = styled.div`
  font-size: 24px;
  font-weight: 500;
`;

const Description = styled.div`
  margin-bottom: 25px;
  
  h3 {
    font-size: 16px;
    margin-bottom: 10px;
    font-weight: 500;
  }
  
  p {
    color: #666;
    line-height: 1.6;
  }
`;

const SpecsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 25px;
  
  th, td {
    padding: 10px 15px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    font-weight: 500;
    width: 40%;
    color: #666;
  }
  
  td {
    color: #333;
  }
  
  tr:last-child th, tr:last-child td {
    border-bottom: none;
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const Tag = styled.span`
  padding: 5px 12px;
  background-color: #f0f0f0;
  color: #666;
  border-radius: 30px;
  font-size: 14px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  padding: 20px;
  background-color: #fdecea;
  color: #e74c3c;
  text-align: center;
  border-radius: 8px;
`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/products/${id}`);
        setProduct(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to load product details");
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [id]);
  
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/products/${id}`);
      navigate("/product");
    } catch (err) {
      alert("Failed to delete product. Please try again.");
    }
  };
  
  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading product details...</LoadingContainer>
      </Container>
    );
  }
  
  if (error || !product) {
    return (
      <Container>
        <ErrorContainer>{error || "Product not found"}</ErrorContainer>
      </Container>
    );
  }
  
  return (
    <Container>
      <TopBar>
        <BackButton onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back to Products
        </BackButton>
        <ActionButtons>
          <EditButton onClick={() => navigate(`/edit-product/${id}`)}>
            <FaEdit /> Edit
          </EditButton>
          <DeleteButton onClick={handleDelete}>
            <FaTrash /> Delete
          </DeleteButton>
        </ActionButtons>
      </TopBar>
      
      <ProductGrid>
        <ImageSection>
          {product.imageUrl ? (
            <ProductImage 
              src={product.imageUrl} 
              alt={product.name} 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/400x400?text=No+Image';
              }}
            />
          ) : (
            <NoImage>
              <FaImage />
              <p>No image available</p>
            </NoImage>
          )}
        </ImageSection>
        
        <InfoSection>
          <ProductName>{product.name}</ProductName>
          
          {product.category && (
            <CategoryBadge>{product.category}</CategoryBadge>
          )}
          
          <PriceSection>
            <Price>
              {product.discount > 0 && product.mrp > product.price && (
                <span className="original-price">₹{product.mrp}</span>
              )}
              ₹{product.price}
            </Price>
          </PriceSection>
          
          <Description>
            <h3>Description</h3>
            <p>{product.description || "No description available"}</p>
          </Description>
          
          <div>
            <h3>Specifications</h3>
            <SpecsTable>
              <tbody>
                <tr>
                  <th>Stock</th>
                  <td>{product.stock} units</td>
                </tr>
                {product.materialType && (
                  <tr>
                    <th>Material</th>
                    <td>{product.materialType}</td>
                  </tr>
                )}
                {product.dimensions && (
                  <tr>
                    <th>Dimensions</th>
                    <td>{product.dimensions}</td>
                  </tr>
                )}
                {product.capacity && (
                  <tr>
                    <th>Capacity</th>
                    <td>{product.capacity} ml</td>
                  </tr>
                )}
                {product.glaze && (
                  <tr>
                    <th>Glaze</th>
                    <td>{product.glaze}</td>
                  </tr>
                )}
                {product.firingTemp && (
                  <tr>
                    <th>Firing Temperature</th>
                    <td>{product.firingTemp}°C</td>
                  </tr>
                )}
                {product.coneType && (
                  <tr>
                    <th>Cone Type</th>
                    <td>{product.coneType}</td>
                  </tr>
                )}
              </tbody>
            </SpecsTable>
          </div>
          
          {product.tags && product.tags.length > 0 && (
            <div>
              <h3>Tags</h3>
              <TagsContainer>
                {product.tags.map((tag, index) => (
                  <Tag key={index}>{tag}</Tag>
                ))}
              </TagsContainer>
            </div>
          )}
        </InfoSection>
      </ProductGrid>
    </Container>
  );
};

export default ProductDetail; 